import React, { useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { fmtDate, fmtTime, minutesToHm } from '@hrms/shared';
import { api } from '../../../lib/api';
import { todayISO } from '../../../lib/date';
import { Avatar, Badge, DataTable, DateField, Grid, PageHeader, Row, Screen, StatCard, StatusBadge, Text } from '../../../ui';

/** Who in my team is in today (or on any past date). */
export function TeamAttendanceScreen() {
  const [date, setDate] = useState(todayISO());
  const q = useQuery({ queryKey: ['attendance', 'team', date], queryFn: () => api.attendance.team(date) });
  const rows = q.data ?? [];
  const cnt = (s: string) => rows.filter((r) => r.day?.status === s).length;
  return (
    <Screen refreshing={q.isRefetching} onRefresh={() => q.refetch()} testID="team-attendance">
      <PageHeader title="Team attendance" subtitle={fmtDate(date)} />
      <View style={{ maxWidth: 260 }}>
        <DateField label="Date" value={date} max={todayISO()} onChange={(d) => d && setDate(d)} testID="team-attendance-date" />
      </View>
      <Grid min={150}>
        <StatCard label="Present" value={cnt('present') + cnt('on_duty')} icon="check" tone="success" />
        <StatCard label="Absent" value={cnt('absent')} icon="x" tone="danger" />
        <StatCard label="On leave" value={cnt('leave') + cnt('half_leave')} icon="leaf" tone="info" />
        <StatCard label="Late" value={rows.filter((r) => r.day?.is_late).length} icon="clock" tone="warning" />
        <StatCard label="Not marked" value={rows.filter((r) => !r.day).length} icon="alert" tone="neutral" />
      </Grid>
      <DataTable
        rows={rows}
        loading={q.isLoading}
        keyExtractor={(r) => r.employee.id}
        emptyTitle="No team members"
        columns={[
          {
            key: 'employee',
            title: 'Employee',
            width: 200,
            flex: 2,
            sortValue: (r) => r.employee.full_name,
            render: (r) => (
              <Row gap={8} wrap={false}>
                <Avatar name={r.employee.full_name} size={28} />
                <View style={{ flexShrink: 1 }}>
                  <Text weight="medium" numberOfLines={1}>{r.employee.full_name}</Text>
                  <Text variant="small" numberOfLines={1}>{r.employee.designation ?? r.employee.emp_code}</Text>
                </View>
              </Row>
            ),
          },
          { key: 'status', title: 'Status', width: 110, sortValue: (r) => r.day?.status ?? 'zz', render: (r) => (r.day ? <StatusBadge status={r.day.status} /> : <Badge>Not marked</Badge>) },
          { key: 'in', title: 'In', width: 80, sortValue: (r) => r.day?.first_in, render: (r) => fmtTime(r.day?.first_in) },
          { key: 'out', title: 'Out', width: 80, sortValue: (r) => r.day?.last_out, render: (r) => fmtTime(r.day?.last_out) },
          { key: 'worked', title: 'Worked', width: 90, align: 'right', sortValue: (r) => r.day?.worked_minutes ?? 0, render: (r) => minutesToHm(r.day?.worked_minutes) },
          { key: 'late', title: 'Late', width: 80, align: 'right', sortValue: (r) => r.day?.late_minutes ?? 0, render: (r) => (r.day?.is_late ? <Badge tone="warning">{`${r.day.late_minutes}m`}</Badge> : '—') },
          { key: 'ot', title: 'OT', width: 80, align: 'right', sortValue: (r) => r.day?.ot_minutes ?? 0, render: (r) => minutesToHm(r.day?.ot_minutes) },
        ]}
      />
    </Screen>
  );
}
